/**
 * Syncs Recharge selling_plan between main PDP form and sticky bar; updates sticky price only.
 * Updates hero price and sticky ATC state on variant change; syncSellingPlan updates sticky price and selling_plan.
 * Expects window.productSellingPlanSyncConfig from snippets/product-selling-plan-sync.liquid.
 * Uses window.theme.moneyFormat (theme-setup.liquid) - does not overwrite Shopify.formatMoney.
 */
(function () {
  let teardownSellingPlanSync = null;

  function formatMoneyCents(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    const theme = window.theme || {};
    const formatString = theme.moneyFormat || '${{amount}}';
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    if (!match) {
      return String(cents);
    }

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) {
        return '0';
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';

      return dollars + centsPart;
    }

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function formatStickyPrice(cents) {
    const formatted = formatMoneyCents(cents);
    const numCents = Number(cents);
    if (isNaN(numCents) || numCents % 100 !== 0) {
      return formatted;
    }
    // Strip a whole-dollar fractional part regardless of currency symbol position.
    return formatted.replace(/([.,])00(?!\d)/g, '');
  }

  function getBasePriceCents(cfg) {
    if (cfg.baseVariantPriceMoney != null && cfg.baseVariantPriceMoney !== '') {
      return Number(cfg.baseVariantPriceMoney);
    }
    const variants = cfg.productJson;
    if (variants && variants.length) {
      return Number(variants[0].price);
    }
    return 0;
  }

  function updateStickyBarForVariant(variant) {
    const cfg = window.productSellingPlanSyncConfig;
    if (!cfg || !cfg.stickyFormId || !variant) {
      return;
    }

    const stickyForm = document.getElementById(cfg.stickyFormId);
    if (!stickyForm) {
      return;
    }

    const btn = stickyForm.querySelector('.js-pdp-fixed-submit');
    if (!btn) {
      return;
    }

    const labelEl = btn.querySelector('.js-pdp-fixed-label');
    const labels = cfg.stickyBarLabels || { addToCart: 'Add to Cart', soldOut: 'Out of stock' };

    if (variant.available) {
      btn.type = 'submit';
      btn.name = 'add';
      btn.disabled = false;
      btn.classList.add('product-form__cart-submit');
      btn.classList.remove('ss-btn-waitlist-trigger');
      if (cfg.enableAjax) {
        btn.setAttribute('aria-haspopup', 'dialog');
        btn.setAttribute('data-add-to-cart', '');
      }
      if (labelEl) {
        labelEl.textContent = labels.addToCart;
      }
    } else {
      btn.type = 'button';
      btn.removeAttribute('name');
      btn.disabled = true;
      btn.classList.remove('product-form__cart-submit');
      btn.classList.add('ss-btn-waitlist-trigger');
      btn.removeAttribute('aria-haspopup');
      btn.removeAttribute('data-add-to-cart');
      if (labelEl) {
        labelEl.textContent = labels.soldOut;
      }
    }
  }

  function applyVariantSelection(variant) {
    if (!variant) {
      return;
    }

    const cfg = window.productSellingPlanSyncConfig;
    if (!cfg) {
      return;
    }

    cfg.baseVariantPriceMoney = variant.price;

    const mainPriceEl = document.querySelector('.pdp-hero-main-price');
    if (mainPriceEl) {
      mainPriceEl.textContent = formatStickyPrice(variant.price);
    }

    updateStickyBarForVariant(variant);
    syncSellingPlan();
  }

  function syncSellingPlan() {
    const cfg = window.productSellingPlanSyncConfig;
    if (!cfg || !cfg.productFormId) {
      return;
    }

    const productForm = document.getElementById(cfg.productFormId);
    const stickyForm = cfg.stickyFormId ? document.getElementById(cfg.stickyFormId) : null;
    if (!productForm) {
      return;
    }

    const basePrice = getBasePriceCents(cfg);
    const stickyPriceEl = stickyForm ? stickyForm.querySelector('.pdp-fixed__price') : null;

    function setStickyBasePrice() {
      if (stickyPriceEl) {
        stickyPriceEl.textContent = formatStickyPrice(basePrice);
      }
    }

    const mainSellingPlanInput = productForm.querySelector('input[name="selling_plan"]');

    if (!mainSellingPlanInput) {
      setStickyBasePrice();
      return;
    }

    if (!mainSellingPlanInput.value) {
      if (stickyForm) {
        const stickySP = stickyForm.querySelector('input[name="selling_plan"]');
        if (stickySP) {
          stickySP.remove();
        }
      }
      setStickyBasePrice();
      return;
    }

    const sellingPlan = (cfg.selling_plans || []).find(function (plan) {
      return plan.id === Number(mainSellingPlanInput.value);
    });
    let price = formatStickyPrice(basePrice);
    if (sellingPlan) {
      switch (sellingPlan.value_type) {
        case 'percentage':
          price = formatStickyPrice((basePrice * (100 - sellingPlan.value)) / 100);
          break;
        case 'price':
          price = formatStickyPrice(sellingPlan.value);
          break;
        case 'fixed_amount':
        default:
          price = formatStickyPrice(basePrice - sellingPlan.value);
          break;
      }
    }

    if (stickyPriceEl) {
      stickyPriceEl.textContent = price;
    }

    if (stickyForm) {
      let stickySellingPlanInput = stickyForm.querySelector('input[name="selling_plan"]');
      if (!stickySellingPlanInput) {
        stickySellingPlanInput = document.createElement('input');
        stickySellingPlanInput.type = 'hidden';
        stickySellingPlanInput.name = 'selling_plan';
        stickyForm.insertBefore(stickySellingPlanInput, stickyForm.firstChild);
      }
      stickySellingPlanInput.value = mainSellingPlanInput.value;
    }
  }

  function attachSellingPlanSync() {
    const cfg = window.productSellingPlanSyncConfig;
    if (!cfg || !cfg.productFormId) {
      return;
    }

    if (teardownSellingPlanSync) {
      return;
    }

    const productForm = document.getElementById(cfg.productFormId);
    const stickyForm = cfg.stickyFormId ? document.getElementById(cfg.stickyFormId) : null;
    if (!productForm) {
      return;
    }

    let formObserver = null;
    let syncInterval = null;
    let clearIntervalTimeout = null;
    let debouncedSyncTimer = null;

    function scheduleSync(delay) {
      if (debouncedSyncTimer) {
        window.clearTimeout(debouncedSyncTimer);
      }
      debouncedSyncTimer = window.setTimeout(function () {
        debouncedSyncTimer = null;
        syncSellingPlan();
      }, delay);
    }

    function onRechargeSellingPlanChange() {
      scheduleSync(100);
    }

    function onDocumentChange(e) {
      if (e.target && e.target.name === 'selling_plan') {
        scheduleSync(50);
      }
    }

    function onDocumentClick(e) {
      const rechargeWidget = document.querySelector('recharge-subscription-widget');
      if (rechargeWidget && rechargeWidget.contains(e.target)) {
        scheduleSync(200);
      }
    }

    function onStickyFormSubmit() {
      syncSellingPlan();
    }

    formObserver = new MutationObserver(function () {
      scheduleSync(50);
    });

    formObserver.observe(productForm, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value'],
    });

    document.addEventListener('recharge:sellingPlanChange', onRechargeSellingPlanChange);
    document.addEventListener('change', onDocumentChange);
    document.addEventListener('click', onDocumentClick);

    if (stickyForm) {
      stickyForm.addEventListener('submit', onStickyFormSubmit);
    }

    scheduleSync(1000);
    syncInterval = window.setInterval(syncSellingPlan, 2000);
    clearIntervalTimeout = window.setTimeout(function () {
      if (syncInterval) {
        window.clearInterval(syncInterval);
        syncInterval = null;
      }
    }, 30000);

    syncSellingPlan();

    teardownSellingPlanSync = function () {
      if (formObserver) {
        formObserver.disconnect();
        formObserver = null;
      }
      document.removeEventListener('recharge:sellingPlanChange', onRechargeSellingPlanChange);
      document.removeEventListener('change', onDocumentChange);
      document.removeEventListener('click', onDocumentClick);
      if (stickyForm) {
        stickyForm.removeEventListener('submit', onStickyFormSubmit);
      }
      if (syncInterval) {
        window.clearInterval(syncInterval);
        syncInterval = null;
      }
      if (clearIntervalTimeout) {
        window.clearTimeout(clearIntervalTimeout);
        clearIntervalTimeout = null;
      }
      if (debouncedSyncTimer) {
        window.clearTimeout(debouncedSyncTimer);
        debouncedSyncTimer = null;
      }
    };
  }

  function bindVariantSelector() {
    const cfg = window.productSellingPlanSyncConfig;
    if (!cfg || !cfg.productJson || cfg._variantSelectorBound) {
      return;
    }

    const variantSelector = document.querySelector('.js-variant-selector');
    if (!variantSelector) {
      return;
    }

    cfg._variantSelectorBound = true;

    variantSelector.addEventListener('change', function () {
      const selectedVariant = variantSelector.value;
      document.querySelectorAll('input[name="id"]').forEach(function (input) {
        input.value = selectedVariant;
      });
      const variant = cfg.productJson.find(function (v) {
        return String(v.id) === String(selectedVariant);
      });
      if (variant) {
        applyVariantSelection(variant);
      }
    });
  }

  function bindProductVariantChange() {
    const cfg = window.productSellingPlanSyncConfig;
    if (!cfg || cfg._productVariantChangeBound) {
      return;
    }

    cfg._productVariantChangeBound = true;

    document.addEventListener('product:variant-change', function (e) {
      const detailVariant = e.detail && e.detail.variant;
      if (!detailVariant || detailVariant.id == null) {
        return;
      }

      const config = window.productSellingPlanSyncConfig;
      if (!config || !config.productJson) {
        return;
      }

      const variant =
        config.productJson.find(function (v) {
          return String(v.id) === String(detailVariant.id);
        }) || {
          id: detailVariant.id,
          price: detailVariant.price,
          available: detailVariant.available,
        };

      applyVariantSelection(variant);
    });
  }

  function bootstrap() {
    attachSellingPlanSync();
    bindVariantSelector();
    bindProductVariantChange();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
